import {
  Body,
  ContactEquation,
  Equation,
  FrictionEquation,
  GSSolver,
  World,
} from "p2";

import CustomWorld from "./CustomWorld";

interface EquationExtended extends Equation {
  a: number;
  b: number;
  B: number;
  invC: number;
  lambda: number;
  maxForceDt: number;
  minForceDt: number;
  contactEquations: ContactEquation[];
  frictionCoefficient: number;
}

interface BodyExtended extends Body {
  addConstraintVelocity: () => void;
  resetConstraintVelocity: () => void;
}

/**
 * A custom constraint solver extending p2.js GSSolver with optimized
 * equation solving for physics constraints and contact resolution.
 *
 * ## Performance Optimizations:
 * - **Reduced Iterations**: Uses only 1 iteration instead of default 10 for speed
 * - **Zero Tolerance**: Disables convergence checking to avoid early termination overhead
 * - **Selective Body Processing**: Only processes dynamic bodies that can actually respond
 * - **Friction Separation**: Handles friction equations independently for better control
 * - **Optimized Loops**: Uses efficient iteration patterns to minimize overhead
 *
 * ## Constraint Solving Process:
 * 1. **Prepare Bodies**: Update mass properties for dynamic bodies only
 * 2. **Compute Equations**: Calculate constraint parameters and limits
 * 3. **Handle Friction**: Process friction equations separately with contact normal forces
 * 4. **Solve Constraints**: Iteratively solve all constraint equations
 * 5. **Apply Results**: Add computed constraint velocities to body velocities
 *
 * ## Integration with CustomWorld:
 * The solver is tightly integrated with CustomWorld's body tracking system,
 * allowing it to skip static and kinematic body updates that would otherwise
 * waste computation time.
 *
 * ## Equation Types Handled:
 * - **Contact Equations**: Prevent body penetration
 * - **Friction Equations**: Provide surface friction forces
 * - **Constraint Equations**: Maintain joint and spring relationships
 *
 * ## Configuration Trade-offs:
 * - **Speed vs Accuracy**: Single iteration provides speed but may sacrifice stability
 * - **Suitable for**: Games where approximate physics is sufficient
 * - **Not Suitable for**: Engineering simulations requiring high precision
 */
export default class CustomSolver extends GSSolver {
  equations!: EquationExtended[];
  world!: CustomWorld;

  constructor() {
    super({ iterations: 1, tolerance: 0 });
  }

  setWorld(world: CustomWorld) {
    this.world = world;
  }

  /**
   * Prepares dynamic bodies for constraint solving by updating their mass properties.
   * Static and kinematic bodies are skipped since they don't respond to constraints.
   */
  prepareBodies() {
    for (const body of this.world.dynamicBodies) {
      body.updateSolveMassProperties();
    }
  }

  /**
   * Computes constraint equation parameters for the current timestep.
   * Calculates force limits and constraint compliance for each equation.
   */
  computeEquations(dt: number) {
    for (const eq of this.equations) {
      eq.lambda = 0;
      if (eq.timeStep !== dt || eq.needsUpdate) {
        eq.timeStep = dt;
        eq.update();
      }
      eq.B = eq.computeB(eq.a, eq.b, dt);
      eq.invC = eq.computeInvC(eq.epsilon);

      eq.maxForceDt = eq.maxForce * dt;
      eq.minForceDt = eq.minForce * dt;
    }
  }

  prepareBodies2() {
    for (const body of this.world.dynamicBodies) {
      (body as BodyExtended).resetConstraintVelocity();
    }
  }

  // Add result to velocity
  postBodies() {
    for (const body of this.world.dynamicBodies) {
      (body as BodyExtended).addConstraintVelocity();
    }
  }

  doFriction(dt: number) {
    const maxFrictionIter = this.frictionIterations;
    const equations = this.equations;
    const tolSquared = Math.pow(this.tolerance * this.equations.length, 2);

    if (maxFrictionIter) {
      // Iterate over contact equations to get normal forces
      for (let iter = 0; iter !== maxFrictionIter; iter++) {
        // Accumulate the total error for each iteration.
        let deltalambdaTot = 0.0;

        for (const c of equations) {
          var deltalambda = iterateEquation(c);
          deltalambdaTot += Math.abs(deltalambda);
        }

        this.usedIterations++;

        // If the total error is small enough - stop iterate
        if (deltalambdaTot * deltalambdaTot <= tolSquared) {
          break;
        }
      }

      this.postBodies();

      updateMultipliers(equations, 1 / dt);

      // Set computed friction force
      for (const eq of equations) {
        if (eq instanceof FrictionEquation) {
          var f = 0.0;
          for (var k = 0; k !== eq.contactEquations.length; k++) {
            f += eq.contactEquations[k].multiplier;
          }
          f *= eq.frictionCoefficient / eq.contactEquations.length;
          eq.maxForce = f;
          eq.minForce = -f;

          eq.maxForceDt = f * dt;
          eq.minForceDt = -f * dt;
        }
      }
    }
  }

  doIterations(dt: number) {
    const maxIter = this.iterations;
    const tolSquared = Math.pow(this.tolerance * this.equations.length, 2);

    for (let iter = 0; iter !== maxIter; iter++) {
      // Accumulate the total error for each iteration.
      let deltalambdaTot = 0.0;
      for (const c of this.equations) {
        var deltalambda = iterateEquation(c);
        deltalambdaTot += Math.abs(deltalambda);
      }

      this.usedIterations++;

      // If the total error is small enough - stop iterate
      if (deltalambdaTot * deltalambdaTot < tolSquared) {
        break;
      }
    }
  }

  solve(dt: number, world: World) {
    this.sortEquations();

    const equations = this.equations;
    const Neq = equations.length;

    this.usedIterations = 0;

    if (Neq !== 0) {
      this.prepareBodies();
      this.computeEquations(dt);
      this.prepareBodies2();
      this.doFriction(dt);
      this.doIterations(dt);
      this.postBodies();
      updateMultipliers(equations, 1 / dt);
    }
  }
}

// Sets the .multiplier property of each equation
function updateMultipliers(equations: EquationExtended[], invDt: number) {
  var l = equations.length;
  while (l--) {
    var eq = equations[l];
    eq.multiplier = eq.lambda * invDt;
  }
}

function iterateEquation(eq: EquationExtended) {
  // Compute iteration
  var B = eq.B,
    eps = eq.epsilon,
    invC = eq.invC,
    lambdaj = eq.lambda,
    GWlambda = eq.computeGWlambda(),
    maxForce_dt = eq.maxForceDt,
    minForce_dt = eq.minForceDt;

  var deltalambda = invC * (B - GWlambda - eps * lambdaj);

  // Clamp if we are not within the min/max interval
  var lambdaj_plus_deltalambda = lambdaj + deltalambda;
  if (lambdaj_plus_deltalambda < minForce_dt) {
    deltalambda = minForce_dt - lambdaj;
  } else if (lambdaj_plus_deltalambda > maxForce_dt) {
    deltalambda = maxForce_dt - lambdaj;
  }
  eq.lambda += deltalambda;
  eq.addToWlambda(deltalambda);

  return deltalambda;
}
